import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { AMBIENT_SCRIBE_TRIAL_WITHOUT_CARD_FEATURE } from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import { hideFeatureDialog, setFeatures } from "features/auth/auth-slice";
import { useMedicalScribe } from "features/medical-scribe/use-medical-scribe.hook";
import { getPaywallSupportError } from "features/paywall/paywall-purchase-error.util";
import {
  useActivateAmbientScribeTrialMutation,
  useGetAmbientScribeTrialOfferQuery,
  useLazyGetCurrentUserPaywallFeaturesQuery
} from "services/paywall";

export const NO_CARD_TRIAL_STARTED_MESSAGE =
  "Your free Scribe trial has started.";

/**
 * What the caller still has to present; a `failed` start has already shown the error snackbar.
 * After `started` the caller runs `finishTrialStart`, once it has disabled its button.
 */
export type NoCardTrialStartResult =
  | { outcome: "started" }
  | { outcome: "needs-support"; message: string }
  | { outcome: "failed" };

/**
 * The Scribe purchase screen's trial without a credit card (SN-19868). The backend decides what
 * this user may do (`offer`: start their own trial, or `null` for the card checkout), so the
 * screen never offers what the write would refuse.
 */
export default function useAmbientScribeNoCardTrial() {
  const flags = useFlags();
  const isFlagOn = flags[AMBIENT_SCRIBE_TRIAL_WITHOUT_CARD_FEATURE] === true;
  const dispatch = useAppDispatch();
  const { setIsAmbientScribeFeatureAvailable } = useMedicalScribe();

  // Asked fresh on every mount: a start of this user's own, or a colleague's purchase, changes it.
  const {
    data,
    error,
    isLoading: isLoadingOffer
  } = useGetAmbientScribeTrialOfferQuery(undefined, {
    skip: !isFlagOn,
    refetchOnMountOrArgChange: true
  });
  const [activateTrial, { isLoading: isStarting }] =
    useActivateAmbientScribeTrialMutation();
  const [fetchUserFeatures] = useLazyGetCurrentUserPaywallFeaturesQuery();

  /**
   * Everything after the backend started a trial: from this screen's own start, or from a purchase
   * it answered with the trial instead of a checkout (no usable Stripe customer). Not called by
   * `startTrial`: the caller marks its screen started first, so the button stays disabled through
   * the features refetch.
   */
  const finishTrialStart = useCallback(async () => {
    // `auth.features` is otherwise read once at login. Check `isSuccess`, not `"data" in`: a failed
    // refetch still carries the previous, pre-trial `data`, because RTK keeps it on rejection.
    const features = await fetchUserFeatures();
    if (features.isSuccess && features.data) {
      dispatch(setFeatures(features.data));
    }
    // The purchase dialog the recording widget opens. Closed here, not as a side effect of
    // `setFeatures`, so it closes even when the refresh failed.
    dispatch(hideFeatureDialog(AMBIENT_SCRIBE_FEATURE_KEY));
    // The app-wide flag behind Capture Visit and the chart-note buttons; it is only checked on load.
    setIsAmbientScribeFeatureAvailable(true);
    dispatch(setCustomMessage(NO_CARD_TRIAL_STARTED_MESSAGE));
    dispatch(setOpen(true));
  }, [dispatch, fetchUserFeatures, setIsAmbientScribeFeatureAvailable]);

  const startTrial = useCallback(async (): Promise<NoCardTrialStartResult> => {
    const response = await activateTrial();

    if ("error" in response) {
      // Every 400 here is a sentence written for the practice (already used, role, switched off…).
      const supportError = getPaywallSupportError(response.error);
      if (supportError) {
        return { outcome: "needs-support", message: supportError.message };
      }
      dispatch(setCustomMessage(<ErrorSnackbarContent response={response} />));
      dispatch(setOpen(true));
      return { outcome: "failed" };
    }

    return { outcome: "started" };
  }, [activateTrial, dispatch]);

  // Gated on the flag as well as `skip`, and dropped on an error: RTK keeps the last `data` both
  // when a query is skipped and when a refetch fails.
  const offerData = isFlagOn && !error ? data : undefined;
  // Only the backend's refusal (a 400 with a sentence) is shown, on a disabled button. Any other
  // failure — a 404 from an older backend, a 500, a timeout — falls back to the card checkout, so a
  // blip never hides Purchase from a practice whose trial has lapsed.
  const offerError =
    isFlagOn && error ? (getPaywallSupportError(error)?.message ?? null) : null;

  return {
    isFlagOn,
    offer: offerData?.offer ?? null,
    trialDays: offerData?.trial_days ?? null,
    offerError,
    isLoadingOffer: isFlagOn && isLoadingOffer,
    startTrial,
    finishTrialStart,
    isStarting
  };
}
